<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="550">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="map">Update "{{ map.name }}" Map</h4>
                    <h4 v-else>New Device Diagnostic Payload Mapping</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="12">
                            <v-select v-model="formData['diagnostic_profile_id']" :items="profiles"
                                      density="compact" item-title="name" item-value="id" label="Select Profile"/>
                        </v-col>
                        <v-col class="py-0" cols="12">
                            <v-text-field v-model="formData['name']" autofocus
                                          :rules="[vRules.required,vRules.maxLength(32)]"
                                          density="compact" label="Name"/>
                        </v-col>
                        <v-col class="py-0" cols="12">
                            <v-text-field v-model="formData['module_index']"
                                          :rules="[vRules.required]" density="compact" label="Module Index"
                                          min="0" type="number"/>
                        </v-col>

                        <v-col class="pt-0" cols="12">
                            <p class="mb-2">Fields (json)</p>
                            <prism-editor
                                v-model="content"
                                :highlight="highlighter"
                                class="my-editor"
                                line-numbers
                            ></prism-editor>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="mt-2 px-4 d-flex justify-end">
                    <v-btn map="button" size="small"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {highlight, languages} from "prismjs";
import {PrismEditor} from "vue-prism-editor";
import "prismjs/themes/prism-tomorrow.min.css";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    map: Object,
});
const formData = reactive({
    valid: false,
    name: props.map?.name ?? null,
    fields: props.map?.fields ?? null,
    module_index: props.map?.module_index ?? null,
    diagnostic_profile_id: props.map?.diagnostic_profile_id ?? null,
});
const highlighter = (content) => highlight(content, languages.javascript, 'JSON');
const emits = defineEmits(['update:modelValue', 'addMap', 'updateMap']);
const dialogSwitch = ref(props.modelValue);
const content = ref("{\n\n}");
const profiles = ref([]);
const errors = ref([]);

onMounted(() => {
    fetchProfilesList();
    const fields = props.map?.fields;
    if (fields === null || fields === undefined) {
        return;
    }
    // The API normally returns fields as a JSON string, but a legacy code path or
    // a Laravel json cast can deliver it as an already-decoded object. Handle both
    // so the editor populates instead of silently falling back to the empty template.
    if (typeof fields === 'object') {
        content.value = JSON.stringify(fields, null, 4);
    } else if (typeof fields === 'string') {
        const parsed = validateJSON(fields);
        if (parsed) {
            content.value = JSON.stringify(parsed, null, 4);
        }
    }
})

const validateJSON = (json) => {
    try {
        return JSON.parse(json);
    } catch (error) {
        return false;
    }
};

const fetchProfilesList = () => {
    send('get', `admin/diagnostics/profiles?list=true`)
        .then((response) => {
            profiles.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Diagnostic Profiles List could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    if (!content.value || content.value === "{\n\n}") {
        errors.value.push(`Fields is required`);
        return false;
    }

    const validJson = validateJSON(content.value);
    if (!validJson) {
        errors.value.push(`Invalid JSON`);
        return false;
    }
    formData['fields'] = JSON.stringify(validJson);
    send(`${props.map ? 'put' : 'post'}`,
        `admin/diagnostics/payload-mappings${props.map ? '/' + props.map.id : ''}`,
        formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(props.map ? 'updateMap' : 'addMap', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>

<style>
.my-editor {
    color: #ccc;
    height: 100%;
    padding: 5px;
    font-size: 14px;
    line-height: 1.5;
    min-height: 300px;
    max-height: 500px;
    background: #2d2d2d;
    font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
}

.my-editor .prism-editor__container {
    padding-left: 5px;
    background: #434343;
}

.my-editor .prism-editor__textarea {
    margin-left: 5px;
}

.my-editor .prism-editor__textarea:focus-visible, .my-editor .prism-editor__textarea:focus {
    outline: none;
}
</style>
